import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { getUserCountryCode } from "~/lib/data/country";
import type { MerchantCountry } from "~/lib/types";

type Props = {
  merchantCountries: MerchantCountry[];
  className?: string;
};

export const CountryFilter = ({ merchantCountries, className = "" }: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);

  const availableCountries = [
    ...new Set(merchantCountries.map(mc => mc.merchant_country_code)),
  ].toSorted();

  const currentCountry = searchParams.get("country") ?? "";

  useEffect(() => {
    if (initialized) return;
    setInitialized(true);

    if (searchParams.has("country")) return;

    const detected = getUserCountryCode();
    if (detected && availableCountries.includes(detected)) {
      const params = new URLSearchParams(searchParams.toString());
      params.set("country", detected);
      navigate(`/merchants?${params.toString()}`, { replace: true });
    }
  }, [initialized, searchParams, availableCountries, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const params = new URLSearchParams(searchParams.toString());
    const { value } = e.target;

    if (value) {
      params.set("country", value);
    } else {
      params.delete("country");
    }

    const qs = params.toString();
    navigate(`/merchants${qs ? `?${qs}` : ""}`);
  };

  if (availableCountries.length === 0) return null;

  return (
    <select
      value={currentCountry}
      onChange={handleChange}
      aria-label={t("CountryFilter.filter-by-country")}
      className={`rounded-pill border border-border-shell bg-surface-blank px-4 py-3 font-body text-text-full focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 ${className}`}
    >
      <option value="">{t("CountryFilter.all-countries")}</option>
      {availableCountries.map(code => (
        <option key={code} value={code}>
          {code}
        </option>
      ))}
    </select>
  );
};
